//@flow

import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { TPNProgressBar } from 'dibs-trade-rewards-progress/exports/TPNProgressBar';
import getter from 'dibs-object-getter';
import {
    TPN_TIER_MEMBER,
    TPN_TIER_PLATINUM,
} from 'dibs-trade-rewards-progress/exports/tpnStatusCodes';

import styles from './PointsSummary.scss';

import type { PointsSummary_tradeFirm as TradeFirm } from './__generated__/PointsSummary_tradeFirm.graphql';
import type { PointsSummary_viewer as Viewer } from './__generated__/PointsSummary_viewer.graphql';

type Props = {
    tradeFirm: TradeFirm,
    viewer: Viewer,
};

export function PointsSummaryComponent(props: Props) {
    const { tradeFirm, viewer } = props;

    const currentYearSpend =
        getter(tradeFirm)
            .at('loyalty')
            .at('currentYearSpend')
            .value('amount') || 0;
    const currentStatusMinimumSpend =
        getter(tradeFirm)
            .at('rewards')
            .at('currentProgram')
            .value('minPoints') || 0;
    const currentStatusCode = getter(tradeFirm)
        .at('rewards')
        .at('currentProgram')
        .at('programInfo')
        .value('code');
    const currentStatusName = getter(tradeFirm)
        .at('rewards')
        .at('currentProgram')
        .at('programInfo')
        .value('displayName');
    const nextStatusName = getter(tradeFirm)
        .at('rewards')
        .at('nextProgram')
        .at('programInfo')
        .value('displayName');
    const nextStatusMinimumSpend =
        getter(tradeFirm)
            .at('rewards')
            .at('nextProgram')
            .value('minPoints') || 0;

    const currentStatusMaintained = currentYearSpend >= currentStatusMinimumSpend;
    const pointsNeededToAchieveNextStatus = Math.ceil(nextStatusMinimumSpend - currentYearSpend);

    let statusSummary;
    switch (true) {
        case currentStatusCode === TPN_TIER_MEMBER && !currentYearSpend:
            statusSummary = (
                <span data-tn="status-summary-member-no-spend">
                    <FormattedMessage
                        id="abt.rewards.PointsSummary.memberSummaryNoSpend"
                        defaultMessage="Start earning points to reach {nextStatusName} status"
                        values={{ nextStatusName }}
                    />
                </span>
            );
            break;
        case currentStatusCode === TPN_TIER_MEMBER && currentYearSpend > 0:
            statusSummary = (
                <span data-tn="status-summary-member-spend">
                    <FormattedMessage
                        id="abt.rewards.PointsSummary.memberSummarySpend"
                        defaultMessage="Keep it up! Earn {pointsNeededToAchieveNextStatus} to reach {nextStatusName}."
                        values={{
                            nextStatusName,
                            pointsNeededToAchieveNextStatus: (
                                <FormattedNumber
                                    value={pointsNeededToAchieveNextStatus}
                                    maximumFractionDigits={0}
                                    minimumFractionDigits={0}
                                />
                            ),
                        }}
                    />
                </span>
            );
            break;
        case !currentYearSpend:
            statusSummary = (
                <span data-tn="tpn-summary-no-spend">
                    <FormattedMessage
                        id="abt.rewards.PointsSummary.tpnSummaryNoSpend"
                        defaultMessage="Start earning points to maintain your {currentStatusName} status."
                        values={{ currentStatusName }}
                    />
                </span>
            );
            break;
        case currentStatusMaintained && currentStatusCode !== TPN_TIER_PLATINUM:
            statusSummary = (
                <span data-tn="tpn-summary-status-maintained">
                    <FormattedMessage
                        id="abt.rewards.PointsSummary.tpnSummaryStatusMaintained"
                        defaultMessage="Keep it up! You need {pointsNeededToAchieveNextStatus} points to reach {nextStatusName} status."
                        values={{ nextStatusName, pointsNeededToAchieveNextStatus }}
                    />
                </span>
            );
            break;
        default:
            statusSummary = (
                <span data-tn="status-summary-short-of-current-status">
                    <FormattedMessage
                        id="abt.rewards.PointsSummary.tpnSummaryShortOfCurrentStatus"
                        defaultMessage="Keep it up! You're on track to maintain your {currentStatusName} status."
                        values={{ currentStatusName }}
                    />
                </span>
            );
    }

    return (
        <React.Fragment>
            <BarHeader
                dataTn="points-earned"
                size="xLarge"
                title={
                    <FormattedMessage
                        id="abt.rewards.PointsSummary.pointsEarned2019"
                        defaultMessage="Points Earned in 2019"
                    />
                }
            />
            <div className={styles.pointsLegendWrapper}>
                <div className={styles.pointsLegend}>
                    <FormattedMessage
                        id="abt.rewards.PointsSummary.pointsLegend"
                        defaultMessage="1 point = $1"
                    />
                </div>
            </div>
            <div className={styles.progressBarContainer}>
                <div className={styles.progressBarWrapper}>
                    <TPNProgressBar tradeFirm={(tradeFirm: Object)} viewer={(viewer: Object)} />
                </div>
            </div>
            <div className={styles.yoySynopsis}>{statusSummary}</div>
        </React.Fragment>
    );
}

export const PointsSummary = createFragmentContainer(PointsSummaryComponent, {
    viewer: graphql`
        fragment PointsSummary_viewer on Viewer {
            ...TPNProgressBar_viewer
        }
    `,
    tradeFirm: graphql`
        fragment PointsSummary_tradeFirm on TradeFirmType {
            loyalty {
                currentYearSpend {
                    amount
                }
            }
            rewards {
                currentProgram {
                    minPoints
                    programInfo {
                        code
                        displayName
                    }
                }
                nextProgram {
                    minPoints
                    programInfo {
                        displayName
                    }
                }
            }
            ...TPNProgressBar_tradeFirm
        }
    `,
});
